import { cn } from "@jesus-central-church/shared";
import { Heading } from "@jesus-central-church/ui";
import { SbBlokData } from "@storyblok/js";
import {
  SbBadge,
  SbButton,
  SbCard,
  SbCookieDeclaration,
  SbGrid,
  SbImage,
  SbImageCard,
  SbLink,
  SbSpotifyEmbed,
  SbWeeztixEvent,
  SbYoutubeEmbed,
} from "@storyblok/types/287435740670216/storyblok-components";
import { StoryblokRichtext } from "@storyblok/types/storyblok";
import {
  MARK_TEXT_STYLE,
  NODE_HEADING,
  NODE_LI,
  NODE_OL,
  NODE_PARAGRAPH,
  NODE_UL,
  render,
} from "storyblok-rich-text-react-renderer";
import { BadgeComponent } from "../badge";
import { ButtonComponent } from "../button";
import { CardComponent } from "../cards/card";
import { ImageCardComponent } from "../cards/image-card";
import { WeeztixCardComponent } from "../cards/weeztix-card";
import { CookieDeclarationComponent } from "../cookie-declaration";
import { GridComponent } from "../layouts/grid";
import { LinkComponent } from "../link";
import { ImageComponent } from "../media/image";
import { SpotifyEmbedComponent } from "../media/spotify-emebd";
import { YoutubeEmbedComponent } from "../media/youtube-embed";

interface RichTextRendererProperties {
  document: StoryblokRichtext;
  className?: string;
  pageData: { locale: string };
}

const resolvers = (pageData: { locale: string }) => ({
  nodeResolvers: {
    [NODE_UL]: (children: React.ReactNode) => (
      <ul className="my-4 list-disc pl-6">{children}</ul> // no list-inside
    ),
    [NODE_OL]: (children: React.ReactNode) => (
      <ol className="my-4 list-decimal pl-6">{children}</ol> // 1. 2. 3. ...
    ),
    [NODE_LI]: (children: React.ReactNode) => (
      <li className="mb-1">{children}</li>
    ),
    [NODE_PARAGRAPH]: (
      children: React.ReactNode,
      parameters: { textAlign: string } | undefined,
    ) => (
      <p className={cn(`text-${parameters?.textAlign || "left"}`, "w-full")}>
        {children}
      </p>
    ),
    [NODE_HEADING]: (
      children: React.ReactNode,
      {
        level = 2,
        textAlign,
      }: { level: 1 | 2 | 3 | 4 | 5 | 6; textAlign: string },
    ) => {
      const levels: Record<
        number,
        "h1" | "h2" | "h3" | "h4" | "h5" | "display"
      > = {
        1: "h1",
        2: "h2",
        3: "h3",
        4: "h4",
        5: "h5",
        6: "h5",
      };

      const variant = levels[level] ?? "h2";
      return (
        <Heading
          variant={variant}
          className={cn(`text-${textAlign || "left"}`, "w-full")}
        >
          {children}
        </Heading>
      );
    },
  },
  markResolvers: {
    [MARK_TEXT_STYLE]: (
      children: React.ReactNode,
      parameters: { textAlign: string } | undefined,
    ) => {
      return (
        <span className={cn(`text-${parameters?.textAlign || "left"}`)}>
          {children}
        </span>
      );
    },
  },
  blokResolvers: {
    badge: (properties: SbBadge) => {
      return <BadgeComponent blok={properties} />;
    },
    button: (properties: SbButton) => {
      return <ButtonComponent blok={properties} />;
    },
    link: (properties: SbLink) => {
      return <LinkComponent blok={properties} />;
    },
    grid: (properties: SbGrid) => {
      return <GridComponent blok={properties} pageData={pageData} />;
    },
    card: (properties: SbCard) => {
      return <CardComponent blok={properties} pageData={pageData} />;
    },
    imageCard: (properties: SbImageCard) => {
      return <ImageCardComponent blok={properties} pageData={pageData} />;
    },
    image: (properties: SbImage) => {
      return (
        <div className="flex w-full items-center justify-center py-4 sm:py-6 md:py-8">
          <ImageComponent
            blok={properties as SbImage & SbBlokData}
            imageClassName="mx-auto"
          />
        </div>
      );
    },
    cookieDeclaration: (properties: SbCookieDeclaration) => {
      return (
        <CookieDeclarationComponent blok={properties} pageData={pageData} />
      );
    },
    spotifyEmbed: (properties: SbSpotifyEmbed) => {
      return <SpotifyEmbedComponent blok={properties} />;
    },
    youtubeEmbed: (properties: SbYoutubeEmbed) => {
      return <YoutubeEmbedComponent blok={properties} />;
    },
    weeztixEvent: (properties: SbWeeztixEvent) => {
      return <WeeztixCardComponent blok={properties} />;
    },
  },
});

export const RichTextRenderer = ({
  document,
  className,
  pageData,
}: RichTextRendererProperties) => {
  if (!document) return null;

  return (
    <span className={cn("rich-text prose max-w-none space-y-4", className)}>
      {/* @ts-expect-error - Resolvers is not typed correctly */}
      {render(document, resolvers(pageData))}
    </span>
  );
};
